'use client';

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type CSSProperties,
} from 'react';

type Direction = 'up' | 'down' | 'left' | 'right' | 'scale';

type Props = {
  children: ReactNode;
  delay?: number;
  direction?: Direction;
  duration?: number;
  threshold?: number;
  className?: string;
  style?: CSSProperties;
  as?: keyof JSX.IntrinsicElements;
};

const INITIAL: Record<Direction, string> = {
  up: 'translate3d(0, 30px, 0)',
  down: 'translate3d(0, -20px, 0)',
  left: 'translate3d(24px, 0, 0)',
  right: 'translate3d(-24px, 0, 0)',
  scale: 'scale(0.96)',
};

// useLayoutEffect warns when run on the server; fall back to useEffect there so
// SSR stays quiet. The reveal logic only runs in the browser anyway.
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function ScrollReveal({
  children,
  delay = 0,
  direction = 'up',
  duration = 700,
  threshold = 0,
  className = '',
  style,
  as = 'div',
}: Props) {
  const ref = useRef<HTMLElement | null>(null);
  // Progressive enhancement. The content renders VISIBLE by default — with no
  // JS, a dead IntersectionObserver, or before hydration, it stays readable
  // (crawlers + accessibility, and the failsafe against the P0 "stuck-hidden"
  // bug). The fade-in is applied only to content that starts BELOW the fold,
  // where hiding it first is never seen. `armed` flips on only for those.
  const [shown, setShown] = useState(false);
  const [armed, setArmed] = useState(false);

  useIsoLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const el = ref.current;
    if (!el) return;

    // Reduced motion: never hide, never animate.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setShown(true);
      return;
    }

    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    // Already on-screen — covers the entire post body on tall viewports.
    // Reveal immediately with no hide-flash. This is height-independent: the
    // old 0.15 intersection-ratio threshold could never be met by a wrapper
    // several screens tall, which left long posts permanently invisible.
    if (r.top < vh - 60 && r.bottom > 0) {
      setShown(true);
      return;
    }

    // Below the fold: arm the hidden initial state, then reveal as it scrolls
    // in. threshold 0 fires on the first visible pixel (gated by the -60px
    // bottom rootMargin), so this is also height-independent.
    setArmed(true);
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold, rootMargin: '0px 0px -60px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  // Honor a live reduced-motion toggle — never strand content hidden.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      if (mq.matches) setShown(true);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const hidden = armed && !shown;
  const Tag = as as 'div';
  return (
    <Tag
      ref={ref as never}
      className={className}
      style={{
        ...style,
        opacity: hidden ? 0 : 1,
        transform: hidden ? INITIAL[direction] : 'translate3d(0,0,0) scale(1)',
        transition: `opacity ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </Tag>
  );
}
